import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiLock, FiMail } from 'react-icons/fi';
import API from '../utils/api';

const Login = ({ setAuth }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    if (loginError) {
      setLoginError('');
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email harus diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }

    if (!formData.password) {
      newErrors.password = 'Password harus diisi';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await API.post('/api/auth/login', formData);

      localStorage.setItem('token', response.data.token);

      setAuth({
        isAuthenticated: true,
        user: response.data.user,
        loading: false
      });

      const { role } = response.data.user;

      switch (role) {
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'staff':
          navigate('/staff/dashboard');
          break;
        case 'mechanic':
          navigate('/mechanic/dashboard');
          break;
        case 'customer':
          navigate('/customer/dashboard');
          break;
        case 'owner':
          navigate('/owner/dashboard');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(
        error.response?.data?.message || 'Terjadi kesalahan saat login. Silakan coba lagi.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const inputBase = 'w-full rounded-full border py-3 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2';

  return (
    <div className="min-h-screen bg-white px-4 py-16 sm:px-6">
      <div className="mx-auto w-full max-w-5xl">
        <div className="grid gap-12 border-t border-slate-200 pt-20 lg:grid-cols-[0.8fr_1fr]">
          <div className="max-w-md">
            <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Login</div>
            <h1 className="mt-5 text-4xl font-bold text-slate-950">Masuk ke akun Anda.</h1>
            <p className="mt-5 text-base leading-8 text-slate-600">
              Halaman login saya sederhanakan supaya lebih ringan dilihat dan lebih fokus ke tindakan utama.
            </p>
          </div>

          <div>
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-secondary">Login</h2>
                <p className="mt-1 text-sm text-gray-500">Masuk ke akun anda</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 text-xl font-bold text-slate-900">
                <span className="text-primary">X</span>S
              </div>
            </div>

            {loginError && (
              <div className="mb-6 flex items-start rounded border border-red-200 bg-red-50 p-3 text-sm">
                <FiAlertCircle className="mr-2 mt-0.5 flex-shrink-0 text-red-500" />
                <span className="text-red-700">{loginError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 border-t border-slate-200 pt-6">
              <div>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FiMail className="text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`${inputBase} ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                    placeholder="Email"
                  />
                </div>
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>

              <div>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <FiLock className="text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`${inputBase} ${errors.password ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                    placeholder="Password"
                  />
                </div>
                {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <input
                    id="remember_me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="remember_me" className="ml-2 block text-gray-700">
                    Ingat saya
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-2 flex w-full items-center justify-center rounded-full bg-primary px-4 py-3 font-medium text-white transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-t-2 border-white" />
                    <span>Memproses...</span>
                  </div>
                ) : (
                  <>
                    <span>Masuk</span>
                    <FiArrowRight className="ml-2" />
                  </>
                )}
              </button>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Belum punya akun?{' '}
                  <Link to="/register" className="font-medium text-primary hover:text-primary-dark">
                    Register
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
